import { PersonalShelf, PersonalShelves, PLAYLIST_INIT_OFFSET, PLAYLIST_LIMIT } from '@jwp/ott-common/src/constants';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { GET_FAVOURITES_LIST, type GetFavouritesListData } from '@jwp/ott-common/src/graphql/query/favorite.queries';
import { generatePlaylistPlaceholder } from '@jwp/ott-common/src/utils/collection';
import { isTruthyCustomParamValue } from '@jwp/ott-common/src/utils/common';
import { isScheduledOrLiveMedia } from '@jwp/ott-common/src/utils/liveEvent';
import type { Content } from '@jwp/ott-common/types/config';
import type { Playlist } from '@jwp/ott-common/types/playlist';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useQueries, UseQueryOptions, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';

import { useAppConfig } from './useAppConfig';

const placeholderData = generatePlaylistPlaceholder(30);

export type UsePlaylistResult = {
  data: Playlist | undefined;
  isLoading: boolean;
  isSuccess?: boolean;
  error?: unknown;
}[];

const usePlaylists = (content: Content[], rowsToLoad: number | undefined = undefined, refreshKey?: number) => {
  const queryClient = useQueryClient();
  const apiService = getModule(ApiService);
  const { pathname } = useLocation();

  const favorites = useFavoritesStore((state) => state.getPlaylist());
  const watchHistory = useWatchHistoryStore((state) => state.getPlaylist());
  const { user } = useAccountStore((state) => ({ user: state.user }));
  const favoritesListId = useConfigStore((state) => state.config.features?.favoritesList);
  const { isCognitoEnabled, isKatonCognitoEnabled } = useAppConfig();
  const contentStore = useContentStateStore((s) => s.rows);
  const setShelfState = useContentStateStore((s) => s.setRowState);
  const [remoteFavoritesPlaylist, setRemoteFavoritesPlaylist] = useState<Playlist | undefined>();
  const [remoteFavoritesLoading, setRemoteFavoritesLoading] = useState(false);

  const favoritesRowPresent = useMemo(() => content.some(({ type }) => type === PersonalShelf.Favorites), [content]);
  const useRemoteFavorites = favoritesRowPresent && !!user && !!favoritesListId && (isCognitoEnabled || isKatonCognitoEnabled);

  const { data: remoteFavoritesData, loading: remoteFavoritesQueryLoading } = useQuery<GetFavouritesListData>(GET_FAVOURITES_LIST, {
    variables: { first: PLAYLIST_LIMIT, offset: 0 },
    skip: !useRemoteFavorites,
    fetchPolicy: 'network-only',
  });

  const playlistQueries = useQueries<UseQueryOptions<Playlist | undefined, unknown, Playlist | undefined>[]>(
    content.map((row, index) => {
      const { contentId, type, featured } = row;
      const shelfRow = contentId ? contentStore[`${pathname}:${contentId}`] : undefined;

      const hasPrevLoadedItems = shelfRow?.totalLoaded !== undefined && shelfRow.totalLoaded > 0;
      const pageLimit = hasPrevLoadedItems ? ((shelfRow?.pageOffset ?? 0) + PLAYLIST_LIMIT).toString() : PLAYLIST_LIMIT.toString();
      const pageOffset = (hasPrevLoadedItems ? 1 : PLAYLIST_INIT_OFFSET).toString();

      return {
        enabled: !!contentId && (!rowsToLoad || rowsToLoad > index) && !PersonalShelves.some((pType) => pType === type),
        queryKey: ['playlist', contentId],
        queryFn: async (): Promise<Playlist | undefined> => {
          const isReels = row.custom?.mediaType === 'reels';
          const isVideos = row.custom?.mediaType === 'videos';
          let playlist: Playlist | undefined;

          if (isReels && contentId) {
            playlist = await apiService.getReelsByPlaylistId(contentId, parseInt(pageLimit), (parseInt(pageOffset) - 1) * PLAYLIST_LIMIT);
          } else if (isVideos && contentId) {
            playlist = await apiService.getVideosByPlaylistId(contentId, parseInt(pageLimit), (parseInt(pageOffset) - 1) * PLAYLIST_LIMIT);
          } else {
            playlist = await apiService.getPlaylistById(contentId, { page_limit: pageLimit, page_offset: pageOffset }, featured);
          }

          if (playlist?.links?.next && playlist.feedid) {
            setShelfState(pathname, playlist.feedid, {
              hasNext: true,
            });
          }
          playlist?.playlist?.forEach((playlistItem) => {
            queryClient.setQueryData(['media', playlistItem.mediaid], playlistItem);
          });
          return playlist;
        },
        placeholderData: contentId ? placeholderData : undefined,
        refetchInterval: (data: Playlist | undefined) => {
          if (!data) { return false; }
          const autoRefetch = isTruthyCustomParamValue(data.refetch) || data.playlist.some(isScheduledOrLiveMedia);
          return autoRefetch ? 1000 * 30 : false;
        },
        retry: false,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
      };
    }),
  );

  useEffect(() => {
    playlistQueries.forEach((query) => {
      const data = query.data;
      if (data && data.playlist.some(isScheduledOrLiveMedia)) {
        query.refetch?.();
      }
    });
  }, [refreshKey]);

  const remoteFavoriteMediaIds = useMemo(
    () => remoteFavoritesData?.getFavouritesList?.nodes?.map((node) => node.fJwMediaId) ?? [],
    [remoteFavoritesData],
  );

  useEffect(() => {
    let cancelled = false;

    const fetchRemoteFavorites = async () => {
      if (!useRemoteFavorites) {
        setRemoteFavoritesPlaylist(undefined);
        setRemoteFavoritesLoading(false);
        return;
      }

      if (!remoteFavoriteMediaIds.length || !favoritesListId) {
        setRemoteFavoritesPlaylist({
          kind: 'WATCHLIST',
          feedid: favoritesListId || PersonalShelf.Favorites,
          title: 'Favorites',
          playlist: [],
        } as Playlist);
        setRemoteFavoritesLoading(false);
        return;
      }

      setRemoteFavoritesLoading(true);
      try {
        const mediaItems = await apiService.getMediaByWatchlist(favoritesListId, remoteFavoriteMediaIds);
        if (cancelled) { return; }

        setRemoteFavoritesPlaylist({
          kind: 'WATCHLIST',
          feedid: favoritesListId,
          title: 'Favorites',
          playlist: mediaItems || [],
        } as Playlist);
      } catch (error) {
        if (!cancelled) {
          setRemoteFavoritesPlaylist(undefined);
        }
      } finally {
        if (!cancelled) {
          setRemoteFavoritesLoading(false);
        }
      }
    };

    void fetchRemoteFavorites();

    return () => {
      cancelled = true;
    };
  }, [apiService, favoritesListId, remoteFavoriteMediaIds, useRemoteFavorites]);

  const result: UsePlaylistResult = content.map(({ type }, index) => {
    if (type === PersonalShelf.Favorites) {
      if (useRemoteFavorites) {
        return {
          data: remoteFavoritesPlaylist,
          isLoading: remoteFavoritesQueryLoading || remoteFavoritesLoading,
          isSuccess: !remoteFavoritesQueryLoading,
        };
      }

      return { data: favorites, isLoading: false, isSuccess: true };
    }
    if (type === PersonalShelf.ContinueWatching) { return { data: watchHistory, isLoading: false, isSuccess: true }; }

    const { data, isLoading, isSuccess, error } = playlistQueries[index];

    return {
      data,
      isLoading,
      isSuccess,
      error,
    };
  });

  return result;
};

export default usePlaylists;
